import React, { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Workflow, Key, RefreshCw, Copy, Check, AlertCircle, Users, 
  Calendar, ToggleLeft, ToggleRight, TrendingUp, Clock, Shield,
  ExternalLink, Code, ChevronDown, ChevronUp, Play, Pause
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const N8NIntegration = () => {
  const { isDark } = useTheme();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [showApiDocs, setShowApiDocs] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/n8n/settings`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/n8n/stats`, { credentials: 'include' })
      ]);

      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSettings(settingsData);
        // If this is a fresh setup, show the new key
        if (settingsData.api_key_full) {
          setNewApiKey(settingsData.api_key_full);
        }
      }

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (err) {
      setError('Failed to load settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/n8n/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to update settings');
      }
    } catch (err) {
      setError('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const regenerateKey = async () => {
    if (!confirm('Are you sure you want to regenerate the API key? The old key will stop working immediately.')) {
      return;
    }

    setRegenerating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/n8n/regenerate-key`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setNewApiKey(data.api_key);
        setSettings(prev => ({
          ...prev,
          api_key_preview: data.api_key_preview,
          api_key_created_at: data.created_at
        }));
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to regenerate key');
      }
    } catch (err) {
      setError('Failed to regenerate key');
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="p-6 flex items-center justify-center h-64">
          <div className="text-[#A2FF00]">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              N8N Integration
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Configure automated lead imports from N8N workflows
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Workflow className="w-8 h-8 text-[#A2FF00]" />
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">×</button>
          </div>
        )}

        {/* New API Key Alert */}
        {newApiKey && (
          <div className="bg-[#A2FF00]/10 border border-[#A2FF00]/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Key className="w-5 h-5 text-[#A2FF00] mt-0.5" />
              <div className="flex-1">
                <h3 className="text-[#A2FF00] font-semibold mb-1">New API Key Generated</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
                  Copy this key now - it will not be shown again!
                </p>
                <div className="flex items-center gap-2">
                  <code className={`flex-1 px-3 py-2 rounded ${isDark ? 'bg-[#0A0A0A] text-white' : 'bg-gray-100 text-gray-900'} text-sm font-mono break-all`}>
                    {newApiKey}
                  </code>
                  <button
                    onClick={() => copyToClipboard(newApiKey)}
                    className="p-2 bg-[#A2FF00] text-black rounded hover:bg-[#8EFF00] transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button onClick={() => setNewApiKey(null)} className="text-gray-400 hover:text-white">×</button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#A2FF00]/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-[#A2FF00]" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.today}</p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Leads Today</p>
                </div>
              </div>
            </div>

            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.this_week}</p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>This Week</p>
                </div>
              </div>
            </div>

            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Workflow className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.total}</p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Imports</p>
                </div>
              </div>
            </div>

            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 ${settings?.enabled ? 'bg-green-500/20' : 'bg-red-500/20'} rounded-lg`}>
                  {settings?.enabled ? 
                    <Play className="w-5 h-5 text-green-400" /> : 
                    <Pause className="w-5 h-5 text-red-400" />
                  }
                </div>
                <div>
                  <p className={`text-lg font-bold ${settings?.enabled ? 'text-green-400' : 'text-red-400'}`}>
                    {settings?.enabled ? 'Active' : 'Inactive'}
                  </p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Integration</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Settings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Integration Settings */}
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Integration Settings
              </h2>
            </div>
            <div className="p-6 space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Enable Integration</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Allow N8N to import leads via API
                  </p>
                </div>
                <button
                  onClick={() => updateSettings({ enabled: !settings?.enabled })}
                  disabled={saving}
                  className={`p-1 rounded-full transition-colors ${
                    settings?.enabled 
                      ? 'bg-[#A2FF00] text-black' 
                      : (isDark ? 'bg-gray-700' : 'bg-gray-300')
                  }`}
                >
                  {settings?.enabled ? 
                    <ToggleRight className="w-8 h-8" /> : 
                    <ToggleLeft className="w-8 h-8" />
                  }
                </button>
              </div>

              {/* Round Robin */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Round-Robin Distribution</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Auto-distribute leads evenly across users
                  </p>
                </div>
                <button
                  onClick={() => updateSettings({ round_robin_enabled: !settings?.round_robin_enabled })}
                  disabled={saving}
                  className={`p-1 rounded-full transition-colors ${
                    settings?.round_robin_enabled 
                      ? 'bg-[#A2FF00] text-black' 
                      : (isDark ? 'bg-gray-700' : 'bg-gray-300')
                  }`}
                >
                  {settings?.round_robin_enabled ? 
                    <ToggleRight className="w-8 h-8" /> : 
                    <ToggleLeft className="w-8 h-8" />
                  }
                </button>
              </div>

              {/* Working Days Only */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Working Days Only</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Only process imports Monday-Friday
                  </p>
                </div>
                <button
                  onClick={() => updateSettings({ working_days_only: !settings?.working_days_only })}
                  disabled={saving}
                  className={`p-1 rounded-full transition-colors ${
                    settings?.working_days_only 
                      ? 'bg-[#A2FF00] text-black' 
                      : (isDark ? 'bg-gray-700' : 'bg-gray-300')
                  }`}
                >
                  {settings?.working_days_only ? 
                    <ToggleRight className="w-8 h-8" /> : 
                    <ToggleLeft className="w-8 h-8" />
                  }
                </button>
              </div>

              {/* Daily Cap */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Daily Cap Per User</h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Maximum leads per user per day
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={settings?.daily_cap_per_user || 20}
                    onChange={(e) => updateSettings({ daily_cap_per_user: parseInt(e.target.value) || 20 })}
                    className={`w-24 px-3 py-2 rounded ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-[#A2FF00]/50`}
                  />
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>leads/user/day</span>
                </div>
              </div>

              {/* Target Column */}
              <div>
                <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>Target Column</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-2`}>
                  Where new leads will be placed
                </p>
                <input
                  type="text"
                  value={settings?.target_column || 'New Leads'}
                  onChange={(e) => updateSettings({ target_column: e.target.value })}
                  className={`w-full px-3 py-2 rounded ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-[#A2FF00]/50`}
                />
              </div>
            </div>
          </div>

          {/* API Configuration */}
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                API Configuration
              </h2>
            </div>
            <div className="p-6 space-y-6">
              {/* API Key */}
              <div>
                <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>API Key</h3>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`flex-1 px-3 py-2 rounded ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-100 border-gray-200'} border`}>
                    <code className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm`}>
                      {settings?.api_key_preview || 'Not generated'}
                    </code>
                  </div>
                  <button
                    onClick={regenerateKey}
                    disabled={regenerating}
                    className="flex items-center gap-2 px-3 py-2 bg-[#A2FF00] text-black rounded hover:bg-[#8EFF00] transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
                    {regenerating ? 'Regenerating...' : 'Regenerate'}
                  </button>
                </div>
                {settings?.api_key_created_at && (
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Created: {new Date(settings.api_key_created_at).toLocaleString()}
                  </p>
                )}
              </div>

              {/* API Endpoint */}
              <div>
                <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>API Endpoint</h3>
                <div className="flex items-center gap-2">
                  <div className={`flex-1 px-3 py-2 rounded ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-100 border-gray-200'} border`}>
                    <code className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} text-sm break-all`}>
                      POST {BACKEND_URL}/api/n8n/leads
                    </code>
                  </div>
                  <button
                    onClick={() => copyToClipboard(`${BACKEND_URL}/api/n8n/leads`)}
                    className={`p-2 rounded ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Health Check Endpoint */}
              <div>
                <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>Health Check</h3>
                <div className={`px-3 py-2 rounded ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-100 border-gray-200'} border`}>
                  <code className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm`}>
                    GET {BACKEND_URL}/api/n8n/health
                  </code>
                </div>
              </div>

              {/* Required Header */}
              <div>
                <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>Required Header</h3>
                <div className={`px-3 py-2 rounded ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-100 border-gray-200'} border`}>
                  <code className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm`}>
                    X-API-Key: your-api-key
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Capacity Stats */}
        {stats?.user_stats && stats.user_stats.length > 0 && (
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                User Capacity Today
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.user_stats.map((user) => (
                  <div
                    key={user.user_id}
                    className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-lg p-4`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'} flex items-center justify-center`}>
                        <Users className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.name}</p>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{user.email}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Leads today</span>
                        <span className={isDark ? 'text-white' : 'text-gray-900'}>{user.leads_today} / {user.daily_cap}</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            user.at_capacity ? 'bg-red-500' : 'bg-[#A2FF00]'
                          }`}
                          style={{ width: `${Math.min(100, (user.leads_today / user.daily_cap) * 100)}%` }}
                        />
                      </div>
                      {user.at_capacity && (
                        <p className="text-xs text-red-400">At capacity</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* API Documentation */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg`}>
          <button
            onClick={() => setShowApiDocs(!showApiDocs)}
            className={`w-full px-6 py-4 flex items-center justify-between ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors`}
          >
            <div className="flex items-center gap-3">
              <Code className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                API Documentation
              </h2>
            </div>
            {showApiDocs ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showApiDocs && (
            <div className={`px-6 pb-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <div className="pt-4 space-y-6">
                {/* Import Leads */}
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-2`}>
                    Import Leads
                  </h3>
                  <code className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                    POST /api/n8n/leads
                  </code>
                  <pre className={`${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-100'} rounded p-4 overflow-x-auto text-sm`}>
                    <code className={isDark ? 'text-gray-300' : 'text-gray-700'}>
{`{
  "leads": [
    {
      "company_name": "Acme Corp",      // Required
      "industry": "Technology",
      "website": "https://acme.com",
      "company_address": "123 Main St",
      "city_town": "London",
      "zip_code": "EC1A 1BB",
      "country": "United Kingdom",
      "company_phone": "+44 20 1234 5678",
      "description": "Leading tech company",
      "contacts": [
        {
          "first_name": "John",         // Required
          "last_name": "Doe",
          "email": "john@acme.com",
          "phone": "+44 7700 900000",
          "role": "main_contact",       // main_contact, billing, cmo, cro, ceo, other
          "title": "CEO"
        }
      ],
      "assign_to_email": "sales@yourcompany.com",  // Or use assign_to_user_id
      "region": "EMEA",
      "external_id": "airtable_rec123"  // For duplicate detection
    }
  ],
  "dry_run": false  // Set true to validate without creating
}`}
                    </code>
                  </pre>
                </div>

                {/* Response */}
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-2`}>
                    Response
                  </h3>
                  <pre className={`${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-100'} rounded p-4 overflow-x-auto text-sm`}>
                    <code className={isDark ? 'text-gray-300' : 'text-gray-700'}>
{`{
  "success": true,
  "message": "Processed 1 leads",
  "imported": 1,
  "skipped": 0,
  "results": [
    {
      "company_name": "Acme Corp",
      "status": "created",          // created, skipped, error
      "card_id": "card_abc123",
      "assigned_to": "John Smith"
    }
  ]
}`}
                    </code>
                  </pre>
                </div>

                {/* Get Users */}
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-2`}>
                    Get Available Users
                  </h3>
                  <code className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                    GET /api/n8n/users
                  </code>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Returns list of users with their current capacity. Useful for N8N to determine which user to assign leads to.
                  </p>
                </div>

                {/* Health Check */}
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-2`}>
                    Health Check
                  </h3>
                  <code className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                    GET /api/n8n/health
                  </code>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Returns integration status, whether it is a working day, and current settings.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default N8NIntegration;
